import { Button } from "@dropbox/dig-components/buttons";
import { FileIcon } from "@dropbox/dig-content-icons";
import { Cluster } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronDownLine,
  ChevronUpLine,
  LinkLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { useTheme } from "@emotion/react";
import { openURL } from "@mirage/service-platform-actions";
import i18n from "@mirage/translations";
import { isEmpty, last, take, uniqBy } from "lodash";
import type { SyntheticEvent } from "react";
import React, { useEffect, useState } from "react";
import styles from "./ResultAttachments.module.css";

import type { dash } from "@dropbox/api-v2-client";
import { Overlay } from "@dropbox/dig-components/overlay";
import { Text } from "@dropbox/dig-components/typography";
import { StylableFavicon } from "@mirage/shared/icons/StylableFavicon";
import { DashTruncateWithTooltip } from "@mirage/shared/util/DashTruncateWithTooltip";
import classNames from "classnames";
import { useMultimediaPreview } from "../SearchPage/Multimedia/hooks/useMultimediaPreview";

type ResultAttachmentsProps = {
  attachments: dash.LinkAttachment[];
  className?: string;
};
const MAX_ATTACHMENTS_TO_SHOW = 3;
export const ResultAttachments: React.FC<ResultAttachmentsProps> = ({
  attachments,
  className = styles.attachments,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isDarkMode = useTheme();
  const uniqueAttachments: dash.LinkAttachment[] = uniqBy(
    attachments,
    (attachment) => `${attachment.title}-${attachment.url}`,
  );
  const shouldShowMore = uniqueAttachments.length > MAX_ATTACHMENTS_TO_SHOW;

  if (isEmpty(uniqueAttachments)) {
    return null;
  }

  return (
    <Cluster gap="8" className={className}>
      {shouldShowMore && (
        <Button
          variant="borderless"
          withIconStart={
            <UIIcon src={expanded ? ChevronUpLine : ChevronDownLine} />
          }
          size="small"
          onClick={() => setExpanded(!expanded)}
        >
          {i18n.t(expanded ? "show_less_answer" : "show_more_answer")}
        </Button>
      )}
      {(expanded
        ? uniqueAttachments
        : take(uniqueAttachments, MAX_ATTACHMENTS_TO_SHOW)
      ).map((attachment) => {
        const extension = last(attachment.title?.split("."));
        const iconUrl = attachment.icon
          ? isDarkMode
            ? attachment.icon.dark_png_url
            : attachment.icon.light_png_url
          : "";
        let icon = iconUrl ? (
          <StylableFavicon
            alt={attachment.title ?? ""}
            src={iconUrl}
            classNames={[styles.attachmentIcon]}
            fallback={<UIIcon src={LinkLine} />}
          />
        ) : extension ? (
          <FileIcon
            extension={extension}
            size="small"
            hasWhitespace={false}
            hasBackground={false}
          />
        ) : undefined;
        if (attachment.preview_url) {
          icon = (
            <AttachmentPreviewUrl
              previewUrl={attachment.preview_url}
              fallback={icon}
              title={attachment.title || ""}
            />
          );
        }
        return (
          <Cluster.Item
            role="button"
            tabIndex={0}
            key={attachment.url}
            onClick={(e: SyntheticEvent) => {
              openURL(attachment.url);

              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <div className={styles.attachmentTile}>
              {icon}
              <Text className={styles.attachmentTileLabel} isBold size="small">
                <DashTruncateWithTooltip
                  lines={1}
                  tooltipProps={{ title: attachment.title }}
                >
                  {attachment.title}
                </DashTruncateWithTooltip>
              </Text>
            </div>
          </Cluster.Item>
        );
      })}
    </Cluster>
  );
};

const AttachmentPreviewUrl: React.FC<{
  previewUrl: string;
  fallback: React.ReactNode;
  title: string;
}> = ({ previewUrl, fallback, title }) => {
  const [open, setOpen] = React.useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const { base64Image, base64Loading, hasError } = useMultimediaPreview(
    previewUrl || "",
    256,
  );
  const imgRef = React.useRef(null);

  useEffect(() => {
    if (base64Image) {
      setImageUrl(base64Image);
    }
  }, [base64Image]);

  const showImage = !hasError && !base64Loading && previewUrl && imageUrl;

  return (
    <div>
      <div
        className={classNames(styles.attachmentPreviewContainer, {
          [styles.hideThumbnail]: !showImage,
        })}
        style={{ position: "relative", zIndex: 1 }}
        ref={imgRef}
      >
        <img
          src={base64Image}
          className={styles.attachmentPreviewUrl}
          alt={title}
          onMouseOver={() => {
            setOpen(true);
          }}
          onMouseOut={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        />
      </div>
      {open && (
        <Overlay
          anchorRef={imgRef}
          placement="top"
          offsetDistance={16}
          auto={true}
        >
          <div className={styles.imageOverlayContainer}>
            <img
              src={base64Image}
              alt={title}
              className={styles.imageOverlay}
            />
          </div>
        </Overlay>
      )}
      {!showImage && (
        <div className={styles.attachmentPreviewFallback}>{fallback}</div>
      )}
    </div>
  );
};
